import React, { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Button, Stack, TextField,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';

import { Container, LoadingState, ErrorState } from '@/components/common';
import { useFaqDetail, useUpdateFaqPost } from '@/hooks/boards.hook';
import { successToast, errorToast } from '@/common/toast';

/**
 * FAQ 수정 페이지
 */
const FaqEditPage = () => {
  const { faqId } = useParams();
  const navigate = useNavigate();

  const { data: faq, isLoading, error } = useFaqDetail(faqId);
  const updateFaqMutation = useUpdateFaqPost(faqId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      title: '',
      content: '',
      replyContent: '',
    },
  });

  useEffect(() => {
    if (faq) {
      reset({
        title: faq.title ?? faq.question ?? '',
        content: faq.content ?? faq.answer ?? '',
        replyContent: faq.reply?.content ?? '',
      });
    }
  }, [faq, reset]);

  const onSubmit = useCallback(
    async (data) => {
      try {
        await updateFaqMutation.mutateAsync(data);
        successToast('FAQ가 수정되었습니다.');
        navigate(`/community/faq/detail/${faqId}`);
      } catch (err) {
        errorToast(`FAQ 수정에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    },
    [updateFaqMutation, navigate, faqId]
  );

  if (isLoading) {
    return <LoadingState title="FAQ 로딩 중" />;
  }

  if (error || !faq) {
    return (
      <ErrorState
        title="FAQ 로딩 오류"
        message="FAQ 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={() => navigate('/community/faq')}
      />
    );
  }

  return (
    <Container title="FAQ 수정">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/community/faq/detail/${faqId}`)}
        >
          목록으로
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || updateFaqMutation.isPending}
        >
          저장
        </Button>
      </Stack>

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Controller
          control={control}
          name="title"
          rules={{ required: '제목을 입력해주세요.' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="제목"
              fullWidth
              required
              error={!!errors.title}
              helperText={errors.title?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="content"
          rules={{ required: '질문 내용을 입력해주세요.' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="질문"
              fullWidth
              multiline
              rows={6}
              required
              error={!!errors.content}
              helperText={errors.content?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="replyContent"
          rules={{ required: '답변을 입력해주세요.' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="답변"
              fullWidth
              multiline
              rows={6}
              required
              error={!!errors.replyContent}
              helperText={errors.replyContent?.message}
            />
          )}
        />
      </Box>
    </Container>
  );
};

export default FaqEditPage;
